/**
 * Close an organisation (CLAUDE.md 11.5).
 *
 * The server refuses while a live project remains -- 409
 * `error.workspace.has_projects`, carrying the count -- because "deleting a workspace
 * requires transferring or deleting every project first. An account deletion never
 * silently destroys shared work." That refusal is the whole safety property, so it is
 * shown as the server phrased it, count included, rather than flattened into a
 * generic failure the owner cannot act on.
 *
 * Confirmation is by typing the workspace name, not by a checkbox: this is
 * irreversible and affects other people's access, and the name is the one thing an
 * owner cannot type by reflex.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useDeleteWorkspace, useWorkspace } from '@/api/workspaces'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'

export function DeleteWorkspaceCard() {
  const { t } = useTranslation()
  const { data: workspace } = useWorkspace()
  const [open, setOpen] = useState(false)

  // Owner only, and never a personal workspace -- that one goes with the account
  // (`DELETE /me`), because an account without a workspace must not exist.
  if (!workspace || workspace.is_personal || !workspace.capabilities.includes('workspace:delete')) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.delete_title')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-sm text-text-muted">{t('workspace.delete_hint')}</p>
        <Button variant="danger" onClick={() => setOpen(true)}>
          <Trash2 size={15} aria-hidden />
          {t('workspace.delete_action')}
        </Button>
      </CardBody>

      <DeleteWorkspaceDialog
        workspaceId={workspace.id}
        name={workspace.name}
        open={open}
        onOpenChange={setOpen}
      />
    </Card>
  )
}

function DeleteWorkspaceDialog({
  workspaceId,
  name,
  open,
  onOpenChange,
}: {
  workspaceId: number
  name: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user, refreshWorkspaces, switchWorkspace } = useAuth()
  const remove = useDeleteWorkspace()

  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setTyped('')
          setError(null)
        }
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('workspace.delete_title')}</DialogTitle>
          <DialogDescription>{t('workspace.delete_hint')}</DialogDescription>
        </DialogHeader>

        {error && (
          <p
            role="alert"
            className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            {error}
          </p>
        )}

        <form
          className="space-y-4"
          onSubmit={async (event) => {
            event.preventDefault()
            setError(null)
            try {
              await remove.mutateAsync(workspaceId)
              const remaining = await refreshWorkspaces()
              // Somewhere to land: the personal workspace, which always exists.
              const next = user?.personal_workspace_id ?? remaining[0]?.id
              if (next !== undefined) switchWorkspace(next)
              toast({ title: t('workspace.deleted'), variant: 'success' })
              onOpenChange(false)
              navigate('/projects')
            } catch (caught) {
              setError(caught instanceof ApiError ? caught.localised : String(caught))
            }
          }}
        >
          <Field description={name}>
            <FieldLabel>{t('workspace.delete_confirm')}</FieldLabel>
            <FieldInput
              required
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </Field>

          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              {t('action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={typed.trim() !== name}
              loading={remove.isPending}
            >
              {t('workspace.delete_action')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
